// src/views/public/BoletoPublico.jsx
import React, { useState, useEffect } from "react";
import { getApiBaseUrl } from "../../services/apiBase";

const fmtM = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtD = d => { try{ return new Date(String(d)+"T12:00:00").toLocaleDateString("pt-BR"); }catch{ return d||""; } };

export default function BoletoPublico() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Token from URL: /boleto/:token
  const token = window.location.pathname.split("/boleto/")[1]?.split("/")[0];

  const BASE = getApiBaseUrl();

  useEffect(() => {
    if (!token || token.length < 8) { setErro("Link inválido."); setLoading(false); return; }
    fetch(`${BASE}/boletos/public/${token}`)
      .then(async r => {
        // If server returns HTML directly (htmlBoleto saved), inject it
        const ct = r.headers.get("content-type")||"";
        if (ct.includes("text/html")) {
          const html = await r.text();
          document.open(); document.write(html); document.close();
          return;
        }
        const data = await r.json();
        if (!data.success) throw new Error(data.message);
        setDados(data.boleto);
      })
      .catch(e => setErro(e.message))
      .finally(() => setLoading(false));
  }, [token, BASE]);

  if (loading) return (
    <div style={{textAlign:"center",padding:80,fontFamily:"sans-serif"}}>
      <div style={{fontSize:18,color:"var(--gray-500)"}}>⏳ Carregando boleto...</div>
    </div>
  );

  if (erro) return (
    <div style={{textAlign:"center",padding:80,fontFamily:"sans-serif"}}>
      <div style={{fontSize:48,marginBottom:16}}>❌</div>
      <div style={{fontSize:18,color:"#991B1B"}}>{erro}</div>
      <div style={{fontSize:13,color:"var(--gray-500)",marginTop:8}}>Verifique o link ou entre em contato com a administração.</div>
    </div>
  );

  if (!dados) return null;

  const statusColor = {
    Pago:"#065F46", Vencido:"#991B1B", Gerado:"#1D4ED8", Registrado:"#92400E", Cancelado:"#6B7280"
  };
  const statusBg = {
    Pago:"#D1FAE5", Vencido:"#FEE2E2", Gerado:"#DBEAFE", Registrado:"#FEF3C7", Cancelado:"#F1F5F9"
  };

  return (
    <div style={{background:"#F0F4F8",minHeight:"100vh",padding:"20px 16px",fontFamily:"Helvetica Neue,Arial,sans-serif"}}>
      <div style={{maxWidth:640,margin:"0 auto",background:"var(--bg-surface)",borderRadius:12,
        boxShadow:"0 2px 16px rgba(0,0,0,.12)",overflow:"hidden"}}>

        {/* Header */}
        <div style={{background:"#1B3A6B",color:"#fff",padding:"20px 24px"}}>
          <div style={{fontSize:18,fontWeight:700}}>{dados.nmFantasia||dados.nmCondo}</div>
          <div style={{fontSize:12,opacity:.8,marginTop:4}}>
            {dados.cidade&&dados.uf?`${dados.cidade} — ${dados.uf}`:""} {dados.cnpj?`· CNPJ: ${dados.cnpj}`:""}
          </div>
        </div>

        <div style={{padding:24}}>
          {/* Valor */}
          <div style={{textAlign:"center",background:"var(--bg-highlight)",borderRadius:10,padding:"18px 16px",marginBottom:20}}>
            <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:4}}>Valor do Boleto</div>
            <div style={{fontSize:32,fontWeight:800,color:"#1B3A6B"}}>{fmtM(dados.vlrBoleto)}</div>
            <div style={{fontSize:13,color:"var(--gray-500)",marginTop:4}}>Vencimento: {fmtD(dados.dtVencimento)}</div>
          </div>

          {/* Dados */}
          {[
            {label:"Referência",  value:dados.competencia||"—"},
            {label:"Unidade",     value:`${dados.unidade||""}${dados.bloco?`/Bl.${dados.bloco}`:""}`},
            {label:"Condômino",   value:dados.nmCondomino||"—"},
            {label:"Nosso Número",value:dados.nossoNumero||"—"},
            {label:"Emissão",     value:fmtD(dados.dtEmissao)},
          ].map((r,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              borderBottom:"1px solid var(--border-subtle)",padding:"9px 0",fontSize:13}}>
              <span style={{color:"#888"}}>{r.label}</span>
              <span style={{fontWeight:600}}>{r.value}</span>
            </div>
          ))}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",fontSize:13}}>
            <span style={{color:"#888"}}>Status</span>
            <span style={{background:statusBg[dados.status]||"#F1F5F9",color:statusColor[dados.status]||"#374151",
              padding:"3px 12px",borderRadius:12,fontWeight:700,fontSize:12}}>
              {dados.status}
            </span>
          </div>

          {/* Linha digitável */}
          {dados.linhaDigitavel && (
            <div style={{marginTop:16}}>
              <div style={{fontSize:11,color:"#888",fontWeight:600,marginBottom:4}}>Linha Digitável</div>
              <div style={{background:"var(--bg-surface-alt)",border:"1px solid var(--border-color)",borderRadius:6,
                padding:"12px",fontFamily:"monospace",fontSize:12,wordBreak:"break-all",cursor:"pointer"}}
                onClick={()=>{navigator.clipboard?.writeText(dados.linhaDigitavel); alert("Copiado!");}}
                title="Clique para copiar">
                {dados.linhaDigitavel}
                <div style={{fontSize:10,color:"var(--gray-400)",marginTop:4}}>📋 Clique para copiar</div>
              </div>
            </div>
          )}

          {/* Código de barras */}
          {dados.codigoBarras && (
            <div style={{marginTop:12}}>
              <div style={{fontSize:11,color:"#888",fontWeight:600,marginBottom:4}}>Código de Barras</div>
              <div style={{background:"var(--bg-surface-alt)",border:"1px solid var(--border-color)",borderRadius:6,
                padding:"12px",fontFamily:"monospace",fontSize:11,wordBreak:"break-all",letterSpacing:1}}>
                {dados.codigoBarras}
              </div>
            </div>
          )}

          {/* QR Code PIX */}
          {dados.qrCodePix?.startsWith("data:image") && (
            <div style={{textAlign:"center",background:"var(--bg-success-soft)",border:"1px solid #86EFAC",
              borderRadius:10,padding:20,marginTop:16}}>
              <div style={{fontWeight:700,color:"#166534",marginBottom:12}}>💚 Pague via PIX</div>
              <img src={dados.qrCodePix} alt="QR Code PIX"
                style={{width:180,height:180,display:"block",margin:"0 auto 10px"}}/>
              <div style={{fontSize:12,color:"#166534"}}>Escaneie com o app do seu banco</div>
              {dados.chavePix && (
                <div style={{fontSize:12,color:"#166534",marginTop:6}}>
                  Chave: <strong>{dados.chavePix}</strong>
                </div>
              )}
            </div>
          )}

          {/* Botão imprimir */}
          <button onClick={()=>window.print()}
            style={{display:"block",width:"100%",background:"#1B3A6B",color:"#fff",
              border:"none",padding:14,borderRadius:8,fontSize:15,cursor:"pointer",
              marginTop:20,fontWeight:700}}>
            🖨️ Imprimir / Salvar PDF
          </button>

          <div style={{textAlign:"center",fontSize:11,color:"var(--gray-400)",marginTop:16}}>
            Horizon eCondo · Em caso de dúvidas contate a administração
          </div>
        </div>
      </div>
    </div>
  );
}
